//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useState, useEffect } from 'react';
import dayjs from 'dayjs';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import {
  TextField       ,
  Box             ,
  Button          ,
  IconButton      ,
  Table           ,
  TableHead       ,
  TableBody       ,
  TableRow        ,
  TableCell       ,
  TableFooter     ,
  TablePagination ,
  Typography      ,
  InputAdornment  ,
} from '@mui/material';

import {
  FirstPage          ,
  KeyboardArrowLeft  ,
  KeyboardArrowRight ,
  LastPage           ,
  UnfoldMoreOutlined ,
  Search             ,
  Save               ,
} from '@mui/icons-material';

// === COMPONENTS ===
import {DeleteBatchDialog} from './DeleteBatchDialog/DeleteBatchDialog';

// === API ===
import {
  onMonitorBatches            ,
  removeListener              ,
  workflowMonitorBatchPriority,
  workflowMonitorBatchDelete  ,
  workflowMonitorSettings     ,
  workflowMonitorBatchDownload,
  on,
} from 'api/prokey/prokey.websocket';

//#endregion

//-----------------------------------------------------------------
function TablePaginationActions(props) {
  //-----------------------------------------------------------------
  const { count, page, rowsPerPage, onPageChange } = props;

  const last_page = Math.ceil(count / rowsPerPage) - 1;
  const is_page_first = page <= 0;
  const is_page_final = page >= last_page;

  //-----------------------------------------------------------------
  const _handleClick_PageFirst = (event) => {
    //-----------------------------------------------------------------
    onPageChange(event, 0);
  };

  //-----------------------------------------------------------------
  const _handleClick_PagePrev = (event) => {
    //-----------------------------------------------------------------
    onPageChange(event, page - 1);
  };

  //-----------------------------------------------------------------
  const _handleClick_PageNext = (event) => {
    //-----------------------------------------------------------------
    onPageChange(event, page + 1);
  };

  //-----------------------------------------------------------------
  const _handleClick_PageFinal = (event) => {
    //-----------------------------------------------------------------
    onPageChange(event, Math.max(0, last_page));
  };

  return (
    <Box sx={{ flexShrink: 0 }} >
      <IconButton onClick={ _handleClick_PageFirst } disabled={ is_page_first } aria-label='first page'>
        <FirstPage />
      </IconButton>
      <IconButton onClick={ _handleClick_PagePrev  } disabled={ is_page_first } aria-label='previous page'>
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton onClick={ _handleClick_PageNext  } disabled={ is_page_final } aria-label='next page'>
        <KeyboardArrowRight />
      </IconButton>
      <IconButton onClick={ _handleClick_PageFinal } disabled={ is_page_final } aria-label='last page'>
        <LastPage />
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

//-----------------------------------------------------------------
export function BatchPanel() {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const theme = useTheme();

  const default_page =  0;

  const [ batches     , setBatches     ] = useState( [] );
  const [ total       , setTotal       ] = useState( 0 );
  const [ page        , setPage        ] = useState( default_page );
  const [ rowsPerPage , setRowsPerPage ] = useState( 15 );

  const [ priority       , setPriority      ] = useState( 100 );
  const [ selected_item  , setSelectedItem  ] = useState( null );
  const [ selected_items , setSelectedItems ] = useState( {} );

  const [ is_open_delete , setIsOpenDelete ] = useState( false );

  //#endregion

  //#region [STYLES]

  const _styles = {
    container: {
      height: '100%',
      overflowX: 'auto',
      width: '100%',
    },
    title_container: {
      display: 'flex',
      padding: '0.25rem',
    },
    title: {
      display: 'inline-block',
      padding: '0 1rem',
    },
    search_container: {
      width: '35%',
      zindex: 1,
      marginLeft: 'auto',
    },
    priority_text: {
      marginTop : '0.5rem',
      marginBottom : '0.5rem',
      width  : '10rem',
    },
    priority_button: {
      marginLeft : '1rem',
      height     : '4rem',
    },
    delete_button: {
      marginLeft : '1rem',
      height     : '4rem',
    },
    search_container: {
      display    : 'flex' ,
      width      : '45%'  ,
      zindex     : 1      ,
      marginLeft : 'auto' ,
      alignItems : 'center',
    },
    search: {
      marginLeft : '1rem',
      width  : '100%',
      height : '4rem',
      zindex : 1000,
    },
    table: {
      tableLayout : 'auto',
      width       : '100%',
    },
    table_row: {
      cursor: 'pointer',
    },
    table_header_row: {
      cursor: 'pointer',
      overflowX: 'auto',
    },
    table_header_name: {
      minWidth: '9rem',
    },
    table_header_items: {
      minWidth: '9rem',
    },
    table_header_errors: {
      minWidth: '9rem',
    },
    table_header_percent: {
      minWidth: '9rem',
    },
    table_header_process: {
      minWidth: '9rem',
    },
    table_header_priority: {
      minWidth: '9rem',
    },
    table_header_timestamp: {
      minWidth: '9rem',
    },
    table_pagination: {
      width: '100%',
    },
    button_download: {
      margin: '1rem' ,
      height: '3rem' ,
      width : '14rem',
    },
  }

  const
    CONTAINER = {
      sx: _styles.container,
    },
    TITLE_CONTAINER = {
      sx: _styles.title_container,
    },
    TITLE = {
      sx       : _styles.title,
      variant  : 'h6',
      color    : 'primary',
      children : `Total Batches: ${total ? total : 0}`
    },
    CONTROL_CONTAINER = {
      sx : _styles.control_container,
    },
    PRIORITY_TEXT = {
      sx           : _styles.priority_text,
      variant      : 'outlined',
      label        : 'Priority',
      // type         : 'number'  ,
      defaultValue : priority  ,
      onKeyDown    : onKeyDown_Priority,
      onChange     : onChange_Priority,
      InputLabelProps: {
        shrink: true,
      }
    },
    PRIORITY_BUTTON = {
      sx       : _styles.priority_button,
      color    : 'primary',
      variant  : 'contained',
      disabled : Object.keys(selected_items).length > 0 ? false : true,
      onClick  : () => workflowMonitorBatchPriority( Object.keys(selected_items), priority ),
      children : 'Priority',
    },
    DELETE_BUTTON = {
      sx       : _styles.delete_button,
      color    : 'error',
      variant  : 'contained',
      disabled : Object.keys(selected_items).length > 0 ? false : true,
      onClick  : onClick_BatchDelete,
      children : 'Delete',
    },
    SEARCH_CONTAINER = {
      sx : _styles.search_container,
    },
    SEARCH = {
      sx           : _styles.search,
      id           : 'input-with-icon-textfield',
      variant      : "standard",
      label        : 'Filter Batches (Substing Search)',
      autoComplete : 'off',
      onKeyDown    : onKeyDown_Search,
      size         : "small",
      InputProps   : {
        startAdornment: (
          <InputAdornment position='start'>
            <Search />
          </InputAdornment>
        ),
      }
    },
    TABLE = {
      sx   : _styles.table,
      size : 'small',
      key  : 0,
    },
    TABLE_HEADER_ROW = {
      sx : _styles.table_header_row,
    },
    TABLE_HEADER_NAME = {
      sx : _styles.table_header_name,
      onClick : () => onClick_Header( 'name' ),
    },
    TABLE_HEADER_ITEMS = {
      sx : _styles.table_header_items,
      onClick : () => onClick_Header( 'number_work_items' ),
    },
    TABLE_HEADER_ERRORS = {
      sx : _styles.table_header_errors,
      onClick : () => onClick_Header( 'errors' ),
    },
    TABLE_HEADER_PERCENT = {
      sx : _styles.table_header_percent,
      onClick : () => onClick_Header( 'percent_complete' ),
    },
    TABLE_HEADER_PROCESS = {
      sx : _styles.table_header_process,
      onClick : () => onClick_Header( 'process_name' ),
    },
    TABLE_HEADER_PRIORITY = {
      sx : _styles.table_header_priority,
      onClick : () => onClick_Header( 'priority' ),
    },
    TABLE_HEADER_TIMESTAMP = {
      sx : _styles.table_header_timestamp,
      onClick : () => onClick_Header( 'time_started' ),
    },
    TABLE_PAGINATION = {
      sx                 : _styles.table_pagination,
      colSpan            : 7,
      count              : total ? total : 0,
      page               : page,
      rowsPerPage        : rowsPerPage,
      rowsPerPageOptions : [15, 25, 50, 100, 200, 500],
      SelectProps : {
        inputProps: { 'aria-label': 'rows per page' },
        native: true,
      },
      onPageChange        : handleChangePage,
      onRowsPerPageChange : handleChangeRowsPerPage,
      ActionsComponent    : TablePaginationActions,
    },
    BUTTON_DOWNLOAD = {
      sx       : _styles.button_download,
      variant  : 'contained',
      startIcon: <Save />,
      onClick  : () => workflowMonitorBatchDownload(),
      children : ['Download (.csv)'],
    };

  //#endregion

  //#region [EFFECT]

  useEffect(() => {
    //-----------------------------------------------------------------
    function handleBatches( data ) {
      //-----------------------------------------------------------------
      setRowsPerPage( Object.keys( data.items )?.length )
      setBatches( data.items );
      setTotal  ( data.count );
    }

    //-----------------------------------------------------------------
    function handleBatchDownload( data ){
      //-----------------------------------------------------------------
      const csv_string = createCsvString(data.batches);
      downloadCsv(csv_string);
    };

    onMonitorBatches( handleBatches );
    on( 'WORKFLOW_MONITOR_BATCHES_DOWNLOAD', handleBatchDownload );

    return () => {
      removeListener('WORKFLOW-MONITOR-BATCHES'         , handleBatches       );
      removeListener('WORKFLOW_MONITOR_BATCHES_DOWNLOAD', handleBatchDownload );
    };
  }, []);

  //#endregion

  //#region [EVENTS] 
  
  //-----------------------------------------------------------------
  function onClick_Header(name) {
    //-----------------------------------------------------------------
    workflowMonitorSettings( { sort_header: name } );
  };

  //-----------------------------------------------------------------
  function onClick_Row(event, batch) {
    //-----------------------------------------------------------------
    const item            = batch.name;
    const is_action_multi = event.ctrlKey;
    const is_action_group = event.shiftKey;
    const is_selected     = selected_items.hasOwnProperty( item );
    const is_multiple     = selected_items.length > 1;

    let temp = { ...selected_items };

    if( !is_action_multi ) {
      temp = {};
    }

    if(  is_selected ) {
      delete temp[item]
    }

    if( !is_selected ) {
      temp[item] = item;
    }

    // if( is_action_group && !selected_item ) {      
    //   temp[item] = item;
    // }

    // if( is_action_group &&  selected_item ) {

    // }

    setSelectedItem ( item );
    setSelectedItems( temp );
  };

  //-----------------------------------------------------------------
  function onClick_BatchDelete() {
    //-----------------------------------------------------------------
    setIsOpenDelete( true );
  }

  //-----------------------------------------------------------------
  function onConfirm_BatchDelete() {
    //-----------------------------------------------------------------
    workflowMonitorBatchDelete( Object.keys(selected_items) );
    setIsOpenDelete( false );
  }

  //-----------------------------------------------------------------
  function onAbort_BatchDelete() {
    //-----------------------------------------------------------------
    setIsOpenDelete( false );
  }

  //-----------------------------------------------------------------
  function onKeyDown_Priority(event) {
    //-----------------------------------------------------------------
    const is_numeric = ['0','1','2','3','4','5','6','7','8','9'].includes( event.key );
    const is_valid = is_numeric || event.key === 'Delete' || event.key === 'Backspace' || event.key === 'ArrowLeft' || event.key === 'ArrowRight';
    if( !is_valid ) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  //-----------------------------------------------------------------
  function onChange_Priority(event) {
    //-----------------------------------------------------------------
    const value = event.target.value;
    setPriority( value );
  };

  //-----------------------------------------------------------------
  function onKeyDown_Search(event) {
    //-----------------------------------------------------------------
    const is_submit = event.key === 'Enter';
    if (is_submit) {
      const value = event.target.value;
      workflowMonitorSettings( { search: value } );
    }
  };

  //-----------------------------------------------------------------
  function handleChangePage(event, newPage) {
    //-----------------------------------------------------------------
    setSelectedItems( {} );
    setSelectedItem ( null );

    setPage(newPage);
    workflowMonitorSettings( { page: newPage + 1 } );
  };

  //-----------------------------------------------------------------
  function handleChangeRowsPerPage(event) {
    //-----------------------------------------------------------------
    const myRowsPerPage = parseInt(event.target.value, 10);
    workflowMonitorSettings( { items_per_page: myRowsPerPage } );
    setRowsPerPage(myRowsPerPage);
    setPage(0);
  };

  //#endregion

  //#region [METHOD]

  //-----------------------------------------------------------------
  function createCsvString(data) {
    //-----------------------------------------------------------------
    try {
      if(!data.length || data.length <= 0 ) return;
       
      const headers = Object.keys(data[0]);

      let result = headers.join(',') + '\n';
      for (let row of data) {
        result += Object.values(row).join(',') + '\n';
      }

      return result;
    } catch (error) {
      console.log(error);
    }
  }

  //-----------------------------------------------------------------
  function downloadCsv(csv_string) {
    //-----------------------------------------------------------------
    const file_name     = `batch-info-${ dayjs().format('YYYYMMDD') }.csv`;
    const csv_blob      = new Blob( [csv_string], { type: 'text/csv' } );
    const csv_url       = window.URL.createObjectURL( csv_blob );
    const csv_hyperlink = document.createElement('a');

    csv_hyperlink.setAttribute('href'    , csv_url  );
    csv_hyperlink.setAttribute('download', file_name);
    csv_hyperlink.click();
  };

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <Box { ...CONTAINER }>
        <Box { ...TITLE_CONTAINER } >
          <DeleteBatchDialog is_open={is_open_delete} onConfirm={onConfirm_BatchDelete} onClose={onAbort_BatchDelete} />
          <Typography { ...TITLE            } />
          <Box        { ...SEARCH_CONTAINER }  >
            <TextField { ...PRIORITY_TEXT   } />
            <Button    { ...PRIORITY_BUTTON } />
            <Button    { ...DELETE_BUTTON   } />
            <TextField { ...SEARCH          } />
          </Box>
        </Box>
        <Table {...TABLE } >
          <TableHead>
            <TableRow { ...TABLE_HEADER_ROW } >
              <TableCell { ...TABLE_HEADER_NAME } >
                <UnfoldMoreOutlined /> Batch
              </TableCell>
              <TableCell { ...TABLE_HEADER_ITEMS } >
                <UnfoldMoreOutlined /> Work Items
              </TableCell>
              <TableCell { ...TABLE_HEADER_ERRORS } >
                <UnfoldMoreOutlined /> Errors
              </TableCell>
              <TableCell { ...TABLE_HEADER_PERCENT } >
                <UnfoldMoreOutlined /> % Completed
              </TableCell>
              <TableCell { ...TABLE_HEADER_PROCESS } >
                <UnfoldMoreOutlined /> Process
              </TableCell>
              <TableCell { ...TABLE_HEADER_PRIORITY } >
                <UnfoldMoreOutlined /> Priority
              </TableCell>
              <TableCell { ...TABLE_HEADER_TIMESTAMP } >
                <UnfoldMoreOutlined /> Start Time
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{displayBatchData()}</TableBody>
          <TableFooter>
            <TableRow>
              <Button          { ...BUTTON_DOWNLOAD  } />
              <TablePagination { ...TABLE_PAGINATION } />
            </TableRow>         
          </TableFooter>
        </Table>
      </Box>
    );
  };

  //-----------------------------------------------------------------
  const displayBatchData = () => {
    //-----------------------------------------------------------------
    let result = [];
    batches.forEach((batch) => {

      const time_started = dayjs( batch.time_started * 1000 ).format('YYYY-MM-DD HH:mm A');
      const is_selected = selected_items.hasOwnProperty( batch.name );

      const style = {..._styles.table_row};
      if( is_selected ) style.backgroundColor = theme.palette.primary.main;

      result.push(
        <TableRow key={batch.name} id={batch.name} sx={style} onClick={ (event) => onClick_Row(event, batch)}>
          <TableCell key={0} >{ batch.name              }</TableCell>
          <TableCell key={1} >{ batch.number_work_items }</TableCell>
          <TableCell key={2} >{ batch.errors            }</TableCell>
          <TableCell key={3} >{ batch.percent_complete  }</TableCell>
          <TableCell key={4} >{ batch.process_name      }</TableCell>
          <TableCell key={5} >{ batch.priority          }</TableCell>
          <TableCell key={6} >{ time_started            }</TableCell>
        </TableRow>
      );
    });

    const empty_rows = rowsPerPage - batches.length;
    if (empty_rows > 0) {
      result.push(
        <TableRow key={`empty_row`} style={{ height: 28 * empty_rows }}>
          <TableCell colSpan={7} key={`empty_cell`}></TableCell>
        </TableRow>
      );
    }

    return result;
  };

  //#endregion

  return display();
}
